import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_HOME } from "@/lib/constants";
import type { UserRole } from "@/types/database";
import { Skeleton } from "@/components/ui/Skeleton";

export function RoleRoute({
  allowedRoles,
  children,
}: {
  allowedRoles: UserRole[];
  children: ReactNode;
}) {
  const { role, profile, loading, fallback, refreshProfile } = useAuth();

  if (!loading && fallback && !profile) {
    return (
      <div className="min-h-screen bg-black p-8 flex items-center justify-center">
        <div className="max-w-md rounded-lg border border-[#553f1c] bg-[#3a2a13] p-5 text-center">
          <h1 className="font-display text-lg font-semibold text-warning">
            Account access paused
          </h1>
          <p className="mt-2 text-sm text-muted">
            We could not load your trusted profile yet. Retry once the account
            service is available.
          </p>
          <button
            type="button"
            onClick={() => void refreshProfile()}
            className="mt-4 rounded-md border border-[#553f1c] px-3 py-2 text-sm font-semibold text-warning hover:bg-[#4a3618]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading || !profile) {
    return (
      <div className="min-h-screen p-8 bg-black">
        <Skeleton className="h-8 w-40 mb-4" />
      </div>
    );
  }

  if (!role || !allowedRoles.includes(role)) {
    const home = ROLE_HOME[role ?? "student"] ?? "/login";
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
